import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Save } from "lucide-react";

export const RB_RESTORATION_ORG_ID = "abd9e111-06ca-48d3-bbed-a2c595cd28c1";

const TIERS = [
  { value: "pioneer", label: "🔵 Pioneer ($0–$300K)", defaultPct: 70 },
  { value: "visionary", label: "🟢 Visionary ($300K–$900K)", defaultPct: 80 },
  { value: "conqueror", label: "🟡 Conqueror ($900K–$1.8M)", defaultPct: 90 },
  { value: "legendary", label: "🔴 Legendary ($1.8M–$2.8M)", defaultPct: 100 },
  { value: "ambassador", label: "⚫ Ambassador ($2.8M+)", defaultPct: 100 },
];

interface RowState {
  user_id: string;
  full_name: string;
  email: string | null;
  tierId: string | null;
  tier: string;
  commission_percent: number;
  notes: string;
  dirty: boolean;
}

export function RBRestorationTiersPanel({ orgId }: { orgId: string }) {
  const { toast } = useToast();
  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgId !== RB_RESTORATION_ORG_ID) return;
    load();
  }, [orgId]);

  const load = async () => {
    setLoading(true);
    const [profilesRes, tiersRes] = await Promise.all([
      supabase.from("profiles").select("id, full_name, email").eq("org_id", orgId),
      supabase.from("rb_rep_tiers" as any).select("*").eq("org_id", orgId),
    ]);
    const tierMap = new Map<string, any>();
    (tiersRes.data || []).forEach((t: any) => tierMap.set(t.user_id, t));
    const next: RowState[] = (profilesRes.data || []).map((p: any) => {
      const t = tierMap.get(p.id);
      return {
        user_id: p.id,
        full_name: p.full_name || p.email || "Unknown",
        email: p.email,
        tierId: t?.id || null,
        tier: t?.tier || "pioneer",
        commission_percent: Number(t?.commission_percent ?? 70),
        notes: t?.notes || "",
        dirty: false,
      };
    });
    setRows(next);
    setLoading(false);
  };

  const updateRow = (user_id: string, patch: Partial<RowState>) => {
    setRows((r) => r.map((row) => (row.user_id === user_id ? { ...row, ...patch, dirty: true } : row)));
  };

  const setTier = (user_id: string, tier: string) => {
    const def = TIERS.find((t) => t.value === tier)?.defaultPct ?? 70;
    updateRow(user_id, { tier, commission_percent: def });
  };

  const save = async (row: RowState) => {
    const payload: any = {
      org_id: orgId,
      user_id: row.user_id,
      tier: row.tier,
      commission_percent: row.commission_percent,
      notes: row.notes || null,
      assigned_by: (await supabase.auth.getUser()).data.user?.id,
    };
    const { error } = await supabase
      .from("rb_rep_tiers" as any)
      .upsert(payload, { onConflict: "org_id,user_id" });
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Tier saved", description: `${row.full_name} → ${row.tier} @ ${row.commission_percent}%` });
    await load();
  };

  if (orgId !== RB_RESTORATION_ORG_ID) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          RB Restoration — Sales Rep Tiers
        </CardTitle>
        <CardDescription>
          Each rep is paid a percent of the <strong>profit</strong> (sale price − your cost) on every job.
          Set each rep's career tier and commission rate. Defaults: Pioneer 70%, Visionary 80%, Conqueror 90%, Legendary/Ambassador 100%.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading team…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">No team members yet. Invite reps and they'll appear here.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.user_id} className="rounded-lg border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{row.full_name}</div>
                    <div className="text-xs text-muted-foreground">{row.email}</div>
                  </div>
                  <Badge variant={row.tierId ? "default" : "secondary"}>
                    {row.tierId ? "Assigned" : "Default"}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Tier</Label>
                    <Select value={row.tier} onValueChange={(v) => setTier(row.user_id, v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIERS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Commission % of profit</Label>
                    <Input
                      type="number"
                      min={0}
                      max={200}
                      step={1}
                      value={row.commission_percent}
                      onChange={(e) => updateRow(row.user_id, { commission_percent: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input
                      value={row.notes}
                      onChange={(e) => updateRow(row.user_id, { notes: e.target.value })}
                      placeholder="e.g. Override agreement"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" disabled={!row.dirty} onClick={() => save(row)} className="gap-2">
                    <Save className="w-4 h-4" /> Save
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
